import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, FileText, Download, Search, Filter, BookOpen, 
  GraduationCap, Calendar, User, Eye, Loader2, FolderOpen, Home
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stage/Year options
const STAGES = [
  { value: 'all', label: 'All Stages' },
  { value: '7-plus', label: '7+' },
  { value: '11-plus', label: '11+' },
  { value: 'year-9', label: 'Year 9' },
  { value: 'year-10', label: 'Year 10' },
  { value: 'gcse', label: 'GCSE' },
  { value: 'a-level', label: 'A-Level' },
];

// Subject options
const SUBJECTS = [
  { value: 'all', label: 'All Subjects' },
  { value: 'maths', label: 'Mathematics' },
  { value: 'english', label: 'English' },
  { value: 'science', label: 'Science' },
  { value: 'physics', label: 'Physics' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'biology', label: 'Biology' },
  { value: 'verbal-reasoning', label: 'Verbal Reasoning' },
  { value: 'non-verbal-reasoning', label: 'Non-Verbal Reasoning' },
  { value: 'economics', label: 'Economics' },
  { value: 'history', label: 'History' },
  { value: 'geography', label: 'Geography' },
];

// Paper type options
const PAPER_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'practice', label: 'Practice Paper' },
  { value: 'mock', label: 'Mock Exam' },
  { value: 'past-paper', label: 'Past Paper' },
  { value: 'worksheet', label: 'Worksheet' },
];

const TutorPapers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [papers, setPapers] = useState([]);
  const [filteredPapers, setFilteredPapers] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadPapers();
  }, []);

  useEffect(() => {
    filterPapers();
  }, [papers, searchQuery, selectedStage, selectedSubject, selectedType]);

  const loadPapers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/papers/approved`, { headers });
      setPapers(response.data.papers || []);
    } catch (error) {
      console.error('Error loading papers:', error);
      toast.error('Failed to load papers');
    } finally {
      setLoading(false);
    }
  };

  const filterPapers = () => {
    let filtered = [...papers];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.topic?.toLowerCase().includes(query)
      );
    }

    // Stage filter
    if (selectedStage !== 'all') {
      filtered = filtered.filter(p => p.stage === selectedStage);
    }

    // Subject filter
    if (selectedSubject !== 'all') {
      filtered = filtered.filter(p => p.subject === selectedSubject);
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(p => p.paperType === selectedType);
    }

    setFilteredPapers(filtered);
  };

  const downloadPaper = async (paperId, fileName) => {
    try {
      const response = await axios.get(`${API}/papers/download/${paperId}`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'paper.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Paper downloaded!');
    } catch (error) {
      console.error('Error downloading paper:', error);
      toast.error('Failed to download paper');
    }
  };

  const viewPaper = async (paperId) => {
    try {
      const response = await axios.get(`${API}/papers/view/${paperId}`, {
        headers,
        responseType: 'blob'
      });
      
      // Open in new tab
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing paper:', error);
      toast.error('Failed to open paper');
    }
  };

  const getSubjectLabel = (value) => {
    return SUBJECTS.find(s => s.value === value)?.label || value;
  };

  const getStageLabel = (value) => {
    return STAGES.find(s => s.value === value)?.label || value;
  };

  // Group papers by subject for category view
  const groupedPapers = filteredPapers.reduce((acc, paper) => {
    const subject = paper.subject || 'other';
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(paper);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Navigation and Title */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/20 px-2 sm:px-3"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/categories')}
                  className="text-white hover:bg-white/20 px-2 sm:px-3"
                >
                  <Home className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Home</span>
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 sm:w-7 sm:h-7" />
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold">
                    Tutor Papers
                  </h1>
                  <p className="text-purple-200 text-xs sm:text-sm hidden sm:block">
                    Quality practice materials from verified tutors
                  </p>
                </div>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-0 text-xs sm:text-sm self-start sm:self-auto">
              {filteredPapers.length} Papers
            </Badge>
          </div>
        </div>
      </div>

      {/* Filters - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <Card className="shadow-lg border-0">
          <CardContent className="p-3 sm:p-4">
            {/* Search - Full width on mobile */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search papers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 text-sm"
                  data-testid="search-papers"
                />
              </div>
            </div>

            {/* Filters - Scrollable on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Stage Filter */}
              <Select value={selectedStage} onValueChange={setSelectedStage}>
                <SelectTrigger className="min-w-[100px] sm:w-36 flex-shrink-0 text-xs sm:text-sm" data-testid="stage-filter">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400" />
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(stage => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Subject Filter */}
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="min-w-[110px] sm:w-40 flex-shrink-0 text-xs sm:text-sm" data-testid="subject-filter">
                  <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400" />
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECTS.map(subject => (
                    <SelectItem key={subject.value} value={subject.value}>
                      {subject.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="min-w-[100px] sm:w-36 flex-shrink-0 text-xs sm:text-sm" data-testid="type-filter">
                  <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {PAPER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Papers Grid - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-8">
        {loading ? (
          <div className="flex justify-center py-12 sm:py-20">
            <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-purple-500" />
          </div>
        ) : filteredPapers.length === 0 ? (
          <Card className="shadow-lg border-0">
            <CardContent className="py-10 sm:py-16 text-center px-4">
              <FolderOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Papers Found</h3>
              <p className="text-sm sm:text-base text-gray-500">
                {papers.length === 0 
                  ? "No approved tutor papers available yet. Check back soon!"
                  : "Try adjusting your filters to find more papers."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {/* Grouped by Subject */}
            {Object.entries(groupedPapers).map(([subject, subjectPapers]) => (
              <div key={subject}>
                <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                  {getSubjectLabel(subject)}
                  <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">{subjectPapers.length}</Badge>
                </h2>
                
                {/* Responsive Grid - 1 column on mobile, 2 on md, 3 on lg */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {subjectPapers.map((paper) => (
                    <Card 
                      key={paper.id} 
                      className="shadow-md hover:shadow-lg transition-all border-0 overflow-hidden"
                      data-testid={`paper-card-${paper.id}`}
                    >
                      <div className="h-1.5 sm:h-2 bg-gradient-to-r from-purple-500 to-indigo-500" />
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">
                              {paper.name}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">
                              {paper.description || 'Practice paper from our tutor network'}
                            </p>
                          </div>
                        </div>

                        {/* Badges - Scrollable on mobile */}
                        <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                          <Badge variant="outline" className="text-[10px] sm:text-xs">
                            {getStageLabel(paper.stage)}
                          </Badge>
                          {paper.paperType && (
                            <Badge variant="secondary" className="text-[10px] sm:text-xs">
                              {paper.paperType}
                            </Badge>
                          )}
                          {paper.topic && (
                            <Badge className="bg-purple-100 text-purple-700 text-[10px] sm:text-xs">
                              {paper.topic}
                            </Badge>
                          )}
                        </div>

                        {/* Tutor Info */}
                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                          <User className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="truncate">By {paper.uploadedByName || 'Verified Tutor'}</span>
                        </div>

                        {/* Actions - Stacked on very small screens */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm h-8 sm:h-9"
                            onClick={() => viewPaper(paper.id)}
                            data-testid={`view-paper-${paper.id}`}
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> View
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 text-xs sm:text-sm h-8 sm:h-9"
                            onClick={() => downloadPaper(paper.id, paper.fileName)}
                            data-testid={`download-paper-${paper.id}`}
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden xs:inline">Download</span><span className="xs:hidden">DL</span>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorPapers;
